import { useCallback, useEffect, useRef } from 'react';

import type { MessageRequest, SentMessage } from './useMessageOutbox';

/** How long after the field loses focus what is held goes, so a tap on send still joins it. */
const BLUR_GRACE_MS = 500;

export interface UseSendHoldOptions {
  send: (messageID: string, request: MessageRequest) => void;
  /** How long a text waits for the next, from the last; zero sends at once. */
  windowMs: number;
  /** The longest a text waits, however long text sits in the field. */
  capMs: number;
  /** Called once what was held has gone, with what it said; true when it moved the chat on. */
  onRelease: (messageIDs: string[], said: string[]) => boolean;
}

interface Entry {
  id: string;
  request: MessageRequest | null;
  words?: string;
}

interface Held {
  entries: Entry[];
  /** When the first of them was held, which the cap counts from. */
  since: number;
}

const nothingHeld = (now: number): Held => ({ entries: [], since: now });

/** What the batch said; none when a message in it has no words, so its text does not judge it. */
const wordsOf = (entries: Entry[]) =>
  entries.every((entry) => entry.words !== undefined)
    ? entries.map((entry) => entry.words as string)
    : [];

/**
 * useSendHold keeps what they type back for a quiet window, so an answer
 * split across messages is read as one. Typing starts the window again, text
 * left in the field keeps it open up to the cap, and a voice note or file,
 * the field losing focus, the page being hidden or the chat being put away
 * all send at once.
 *
 * Every message comes through sendMessage with its request, or none where it
 * is only kept, so what is only kept moves the chat on the same way.
 */
export const useSendHold = ({
  send,
  windowMs,
  capMs,
  onRelease,
}: UseSendHoldOptions) => {
  const held = useRef<Held>(nothingHeld(0));
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const draft = useRef('');
  // The latest, so a release from a timer acts on the chat as it is now.
  const onReleaseNow = useRef(onRelease);
  useEffect(() => {
    onReleaseNow.current = onRelease;
  }, [onRelease]);

  const release = useCallback((): boolean => {
    clearTimeout(timer.current);
    const { entries } = held.current;
    held.current = nothingHeld(0);
    if (!entries.length) return false;
    entries.forEach(({ id, request }) => {
      if (request) send(id, request);
    });
    return onReleaseNow.current(
      entries.map((entry) => entry.id),
      wordsOf(entries),
    );
  }, [send]);

  const releaseAt = useCallback(
    (at: number) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const capAt = held.current.since + capMs;
        const isTextLeft = !!draft.current.trim();
        if (isTextLeft && Date.now() < capAt) {
          releaseAt(Math.min(Date.now() + windowMs, capAt));
          return;
        }
        release();
      }, at - Date.now());
    },
    [capMs, release, windowMs],
  );

  const startWindow = useCallback(() => {
    const now = Date.now();
    releaseAt(Math.min(now + windowMs, held.current.since + capMs));
  }, [capMs, releaseAt, windowMs]);

  /** Takes in a message: a text waits for the next, anything else sends with what was held. */
  const sendMessage = useCallback(
    (id: string, request: MessageRequest | null, message: SentMessage) => {
      if (!held.current.entries.length) held.current = nothingHeld(Date.now());
      held.current.entries.push({ id, request, words: message.words });
      if (message.kind !== 'text' || windowMs <= 0) {
        release();
        return;
      }
      startWindow();
    },
    [release, startWindow, windowMs],
  );

  /** Sends what is held at once; true when that moved the chat on. */
  const flush = useCallback(() => release(), [release]);

  const onDraftChange = useCallback(
    (text: string) => {
      draft.current = text;
      if (text.trim() && held.current.entries.length) startWindow();
    },
    [startWindow],
  );

  const onDraftBlur = useCallback(() => {
    if (held.current.entries.length) releaseAt(Date.now() + BLUR_GRACE_MS);
  }, [releaseAt]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') release();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      release();
    };
  }, [release]);

  return { sendMessage, flush, onDraftChange, onDraftBlur };
};
